import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useCallback, useState } from 'react';
import {
  useRemoveSponsorMutation,
  useSaveSponsorMutation,
  useSponsorsQuery,
} from '@/queries/sponsors';
import Portal from '../../modal/Portal';
import { SponsorForm } from '../../sponsors';
import {
  EventRelatedItemRow,
  EventRelatedSection,
} from '../EventRelatedSection';

export function SponsorsSection({ festivalId }) {
  const sponsorsQ = useSponsorsQuery(festivalId);
  const saveMutation = useSaveSponsorMutation(festivalId);
  const removeMutation = useRemoveSponsorMutation(festivalId);

  const sponsors = sponsorsQ.data ?? [];
  const loading =
    festivalId == null || sponsorsQ.isPending || sponsorsQ.isFetching;

  const [showModal, setShowModal] = useState(false);
  const [selectedSponsor, setSelectedSponsor] = useState({});
  const [pendingDelete, setPendingDelete] = useState(null);

  const openModal = (sponsor) => {
    const next =
      sponsor?.sponsorId != null
        ? sponsors.find((s) => s.sponsorId === sponsor.sponsorId) || {}
        : {};
    setSelectedSponsor(next);
    setShowModal(true);
  };

  const onFileUpload = (fileInfo) => {
    const { filename, url } = fileInfo;
    setSelectedSponsor((prev) => ({
      ...prev,
      photoURL: url,
      sponsorPhoto: filename,
    }));
  };

  const onFileDelete = () => {
    setSelectedSponsor((prev) => ({
      ...prev,
      photoURL: null,
      sponsorPhoto: null,
    }));
  };

  const handleSave = async (sponsor) => {
    const updatedSponsor = { ...selectedSponsor, ...sponsor };
    try {
      await saveMutation.mutateAsync(updatedSponsor);
      setShowModal(false);
    } catch {
      /* mutation toast */
    }
  };

  const requestDelete = useCallback((sponsor) => {
    if (!sponsor?.sponsorId) {
      return;
    }
    setPendingDelete(sponsor);
  }, []);

  const cancelDelete = useCallback(() => {
    setPendingDelete(null);
  }, []);

  const confirmDelete = useCallback(async () => {
    const sponsor = pendingDelete;
    if (!sponsor?.sponsorId) {
      return;
    }
    try {
      await removeMutation.mutateAsync(sponsor.sponsorId);
      setPendingDelete(null);
      if (selectedSponsor.sponsorId === sponsor.sponsorId) {
        setShowModal(false);
        setSelectedSponsor({});
      }
    } catch {
      /* mutation toast */
    }
  }, [pendingDelete, removeMutation, selectedSponsor.sponsorId]);

  return (
    <>
      <EventRelatedSection
        icon={['fas', 'handshake']}
        title="Sponsors"
        loading={loading}
        onAdd={() => openModal({})}
        addLabel="Add Sponsor"
      >
        {sponsors.map((sponsor) => (
          <EventRelatedItemRow
            key={sponsor.sponsorId}
            label={sponsor.sponsorName || 'Untitled sponsor'}
            onEdit={() => openModal(sponsor)}
            onDelete={() => requestDelete(sponsor)}
          />
        ))}
      </EventRelatedSection>

      <Portal
        header={selectedSponsor?.sponsorName || 'Add New Sponsor'}
        open={showModal}
        onClose={() => setShowModal(false)}
      >
        <SponsorForm
          sponsor={selectedSponsor}
          handleSave={handleSave}
          onFileUpload={onFileUpload}
          onFileDelete={onFileDelete}
        />
      </Portal>

      <Portal
        header="Delete sponsor"
        open={Boolean(pendingDelete)}
        onClose={cancelDelete}
        size="sm"
      >
        <div className="py-2">
          <p className="m-0 text-base leading-relaxed text-[#121212]">
            Are you sure you want to delete this sponsor?
          </p>
          <div className="mt-6 flex flex-wrap justify-end gap-3">
            <button
              type="button"
              className="inline-flex min-h-11 min-w-[120px] items-center justify-center rounded-[var(--bethere-radius-md)] border-2 border-primary bg-transparent px-5 font-bold text-primary transition-all hover:-translate-y-px hover:bg-primary hover:text-primary-foreground"
              onClick={cancelDelete}
            >
              Cancel
            </button>
            <button
              type="button"
              className="inline-flex min-h-11 min-w-[120px] items-center justify-center gap-2 rounded-[var(--bethere-radius-md)] border border-transparent bg-[#c9302c] px-5 font-bold text-white transition-all hover:-translate-y-px hover:brightness-110 disabled:opacity-60"
              onClick={confirmDelete}
              disabled={removeMutation.isPending}
            >
              <FontAwesomeIcon icon={['fas', 'trash']} />
              Delete
            </button>
          </div>
        </div>
      </Portal>
    </>
  );
}
